import { Box, IconButton, keyframes, styled } from '@mui/material'
import { rgba } from 'polished'

import { statusChipBase } from 'components/Status/Status.styles'

import { theme } from 'styles/theme'

interface ResultsProps {
  hasResults: boolean
}

interface TypeProps {
  color: string
}

interface CategoryChipProps {
  category: string
}

const spinEff = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`

export const SkeletonBase = styled('div')(() => ({
  // Gradiente com contraste perceptível e brilho passando no meio
  background:
    'linear-gradient(90deg, #ECEEEC 0%, #ECEEEC 35%, #F6F8F6 50%, #ECEEEC 65%, #ECEEEC 100%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.6s ease-in-out infinite`,
  borderRadius: 6
}))

export const SkeletonAvatar = styled(SkeletonBase)(() => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  flexShrink: 0
}))

export const SkeletonText = styled(SkeletonBase)<{ width?: string | number }>(
  ({ width = '100%' }) => ({
    height: 12,
    width: typeof width === 'number' ? `${width}px` : width
  })
)

export const SkeletonChip = styled(SkeletonBase)(() => ({
  width: 70,
  height: 22,
  borderRadius: 9999,
  flexShrink: 0
}))

export const SkeletonResult = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 16px',
  margin: '0 -16px',
  gap: 12
}))

export const SkeletonContent = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: 1
}))

export const SkeletonDetails = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flex: 1
}))

const getCategoryStyles = (category: string) => {
  switch (category) {
    case 'archived_customer':
      return {
        color: theme.palette.neutral[400],
        background: rgba(theme.palette.neutral[700], 0.3)
      }
    case 'inactive_customer':
      return {
        color: theme.palette.alert[300],
        background: rgba(theme.palette.alert[400], 0.3)
      }
    case 'lead':
      return {
        color: theme.palette.secondary[200],
        background: rgba(theme.palette.secondary[200], 0.1)
      }
    case 'active_customer':
      return {
        color: theme.palette.primary[200],
        background: rgba(theme.palette.primary[200], 0.2)
      }
    case 'churned':
      return {
        color: theme.palette.error[300],
        background: rgba(theme.palette.error[300], 0.1)
      }
    case 'lost':
      return {
        color: theme.palette.neutral[300],
        background: rgba(theme.palette.neutral[300], 0.1)
      }
    default:
      return {
        color: theme.palette.neutral[400],
        background: rgba(theme.palette.neutral[700], 0.3)
      }
  }
}

export const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'archived_customer':
      return 'Arquivado'
    case 'inactive_customer':
      return 'Inativo'
    case 'lead':
      return 'Lead'
    case 'active_customer':
      return 'Ativo'
    case 'churned':
      return 'Churned'
    case 'lost':
      return 'Lost'
    default:
      return ''
  }
}

// Geometria vem do token único (`statusChipBase`). O que é local aqui é só o
// alinhamento da lista de resultados: largura mínima igual em todos os chips
// pra as linhas não ficarem "dançando" — não é override de tamanho.
const CHIP_BASE = {
  ...statusChipBase,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: 72,
  width: 'auto',
  textAlign: 'center' as const
}

export const CategoryChip = styled('div')<CategoryChipProps>(
  ({ category }) => ({
    ...CHIP_BASE,
    ...getCategoryStyles(category)
  })
)

// Aplica no <Status> a MESMA largura mínima do CategoryChip, pra os chips da
// lista de resultados ficarem alinhados. Tamanho de fonte/padding NÃO se mexe
// aqui — vem do `statusChipBase`, igual em todo o sistema.
export const StatusChipWrap = styled('div')(() => ({
  display: 'inline-flex',
  '& > div': {
    justifyContent: 'center',
    minWidth: 72,
    width: 'auto',
    textAlign: 'center'
  }
}))

export const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  left: '50%',
  position: 'fixed',
  // Ancorado no topo (mesma distância do topo) e cresce para baixo,
  // dando mais espaço de resultado do que centralizado verticalmente.
  top: '7vh',
  transform: 'translateX(-50%)',
  width: '52%',
  maxWidth: '820px',
  minWidth: '680px',
  height: 'auto',
  ':focus': {
    boxShadow: '0 0 0 0',
    border: '0 none',
    outline: '0'
  },
  [theme.breakpoints.down('lg')]: {
    width: '72%'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 0,
    top: 0,
    left: 0,
    transform: 'translate(0)',
    backgroundColor: theme.palette.neutral[800]
  }
}))

export const Content = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '16px'
}))

export const SearchBox = styled(Box)<ResultsProps>(({ hasResults }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  backgroundColor: 'rgb(255, 255, 255)',
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 11px 15px -7px, rgb(0 0 0 / 14%) 0px 24px 38px 3px, rgb(0 0 0 / 12%) 0px 9px 46px 8px',
  borderRadius: hasResults ? '8px 8px 0px 0px' : '8px',
  boxSizing: 'border-box',
  fontSize: '16px',
  padding: '20px 24px',
  '.spinner': {
    animation: `${spinEff} infinite 5s linear`
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor: 'transparent',
    boxShadow: 'none'
  }
}))

export const ResultsList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  backgroundColor: 'rgb(255, 255, 255)',
  borderRadius: '0px 0px 8px 8px',
  fontSize: '16px',
  padding: '16px 24px',
  boxSizing: 'border-box',
  // Altura FIXA para o modal não mudar de tamanho conforme o número de
  // resultados: mantém a mesma margem do topo (7vh) e do fim da página.
  // Desconta 7vh do topo + 7vh embaixo + ~150px da barra de busca e tabs.
  height: 'calc(100vh - 14vh - 150px)',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    height: '100vh',
    backgroundColor: 'transparent'
  }
}))

// Painel de recentes (mesma "casca" da ResultsList, mas altura automática:
// não precisa fixar como os resultados porque não troca de tamanho com a busca).
export const RecentContainer = styled('div')(() => ({
  position: 'relative',
  top: -4
}))

export const RecentList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  backgroundColor: 'rgb(255, 255, 255)',
  borderRadius: '0px 0px 8px 8px',
  fontSize: '16px',
  padding: '8px 24px 16px',
  boxSizing: 'border-box',
  maxHeight: 'calc(100vh - 14vh - 120px)',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    backgroundColor: 'transparent'
  }
}))

// Chips de buscas recentes em uma linha só: nunca quebra o layout. Se passarem
// da largura, rolam horizontalmente. CSS força overflow-y a 'auto' quando o X
// rola, o que clipa os chips — por isso damos altura folgada via padding
// vertical em vez de confiar em overflow-y: visible.
export const RecentTermsRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 8,
  marginTop: 4,
  marginBottom: 4,
  // Altura folgada o suficiente para o chip + a scrollbar fina, evitando que o
  // overflow-x (que vira overflow-y: auto pelo CSS) corte o topo dos chips.
  minHeight: 42,
  paddingBottom: 4,
  overflowX: 'auto',
  flexShrink: 0,
  scrollbarWidth: 'thin',
  // Cada chip não encolhe; termos longos são limitados por largura.
  '& > *': {
    flexShrink: 0,
    maxWidth: 200
  },
  '::-webkit-scrollbar': { height: 4 },
  '::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.neutral[700],
    borderRadius: 4
  }
}))

export const EmptyState = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
  color: theme.palette.neutral[400],
  textAlign: 'center',
  gap: 8
}))

// Barra de chips de filtro (substitui as abas). No desktop quebra em mais de
// uma linha quando há muitos tipos; no mobile vira uma faixa de rolagem
// horizontal em linha única, evitando a "parede" de tags empilhadas.
export const FilterBar = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  paddingBottom: 12,
  marginBottom: 4,
  [`@media (max-width: 600px)`]: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    // flexShrink:0 + minHeight impedem que o flex-column pai comprima a barra
    // (sem isso o overflow-x colapsa a altura e os chips se sobrepõem).
    flexShrink: 0,
    minHeight: 34,
    alignItems: 'center',
    paddingBottom: 8,
    // Avança até a borda direita do modal (cancela o padding lateral da
    // ResultsList) para o último chip visível ser cortado pela borda da tela —
    // assim um pedaço do próximo chip fica "espiando", sinalizando que rola.
    marginRight: -24,
    paddingRight: 24,
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    // Os chips não encolhem nem comprimem: cada um ocupa a largura do seu texto
    // (width:max-content) e a barra rola horizontalmente em vez de espremê-los.
    '& > *': { flexShrink: 0, width: 'max-content' },
    '::-webkit-scrollbar': { display: 'none' }
  }
}))

interface FilterChipProps {
  isActive: boolean
}

// Espelha o padrão de FilterChip do NotificationCenter (isActive + toggle).
export const FilterChip = styled('button')<FilterChipProps>(({ isActive }) => ({
  all: 'unset',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  padding: '5px 12px',
  borderRadius: 16,
  fontSize: '12px',
  fontWeight: isActive ? 600 : 400,
  minWidth: 0,
  whiteSpace: 'nowrap',
  transition: 'all 150ms linear',
  color: isActive ? theme.palette.primary[100] : theme.palette.neutral[400],
  backgroundColor: isActive ? `${theme.palette.primary[100]}14` : 'transparent',
  border: `1px solid ${
    isActive ? `${theme.palette.primary[100]}40` : theme.palette.neutral[700]
  }`,
  '&:hover': {
    borderColor: isActive
      ? `${theme.palette.primary[100]}60`
      : theme.palette.neutral[600],
    backgroundColor: isActive
      ? `${theme.palette.primary[100]}1A`
      : theme.palette.neutral[800]
  }
}))

// Cabeçalho de cada seção na visão "Tudo".
export const SectionHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 12,
  marginBottom: 4,
  color: theme.palette.neutral[400],
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
}))

// "Ver todas as N ... →" no fim de uma seção que tem mais itens que o limite.
export const SectionSeeAll = styled('button')(() => ({
  all: 'unset',
  cursor: 'pointer',
  display: 'block',
  marginTop: 4,
  marginBottom: 4,
  fontSize: '12px',
  fontWeight: 600,
  color: theme.palette.primary[200],
  ':hover': {
    color: theme.palette.primary[300],
    textDecoration: 'underline'
  }
}))

// "Você quis dizer ...?" exibido no empty state quando o ES sugere um termo.
export const SuggestionText = styled('div')(() => ({
  marginTop: 4,
  fontSize: '13px',
  color: theme.palette.neutral[400]
}))

export const SuggestionButton = styled('button')(() => ({
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 700,
  color: theme.palette.primary[200],
  textDecoration: 'underline',
  ':hover': {
    color: theme.palette.primary[300]
  }
}))

export const CloseButton = styled(IconButton)(() => ({
  width: '30px',
  height: '30px',
  position: 'absolute',
  right: -10,
  top: -10,
  backgroundColor: '#FFF',
  border: `solid 1px ${theme.palette.neutral[700]}`,
  ':hover': {
    backgroundColor: theme.palette.neutral[600]
  },
  [theme.breakpoints.down('sm')]: {
    position: 'inherit'
  }
}))

export const Result = styled('a', {
  shouldForwardProp: (prop) => prop !== 'isActive'
})<{ isActive?: boolean }>(({ isActive }) => ({
  textDecoration: 'none',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '8px',
  padding: '12px 16px',
  margin: '0 -16px',
  transition: 'all 0.15s ease',
  borderLeft: '3px solid transparent',
  // Linha ativa pela navegação por teclado (↑↓): mesmo realce do hover.
  ...(isActive && {
    backgroundColor: theme.palette.neutral[800],
    borderLeftColor: theme.palette.primary[200]
  }),
  ':hover': {
    backgroundColor: theme.palette.neutral[800],
    borderLeftColor: theme.palette.primary[200]
  }
}))

export const ResultTitle = styled('span')(() => ({
  color: theme.palette.neutral[200],
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '1.4',
  '& mark': {
    backgroundColor: 'rgba(86, 187, 77, 0.25)',
    color: 'inherit',
    fontWeight: 700,
    borderRadius: '3px',
    padding: '0 1px'
  }
}))

export const ResultNickname = styled('span')(() => ({
  color: theme.palette.neutral[400],
  fontWeight: 400,
  fontSize: '13px'
}))

export const ResultSubtitle = styled('span')(() => ({
  color: theme.palette.neutral[400],
  fontWeight: 400,
  fontSize: '12px'
}))

// Pista de qual campo extra fez o cliente aparecer na busca ("título: valor").
// Uma linha só, truncada com reticências — nunca pode gerar scroll lateral.
export const CustomFieldMatch = styled('span')(() => ({
  marginTop: 2,
  display: 'block',
  maxWidth: '100%',
  fontSize: '12px',
  color: theme.palette.neutral[400],
  fontStyle: 'italic',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '& mark': {
    backgroundColor: 'rgba(86, 187, 77, 0.25)',
    color: 'inherit',
    fontStyle: 'normal',
    fontWeight: 700,
    borderRadius: '3px',
    padding: '0 1px'
  }
}))

export const ResultDetails = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minWidth: 0
}))

export const ResultRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}))

export const Avatar = styled('div')(() => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  border: `1px solid ${theme.palette.neutral[400]}`,
  color: theme.palette.neutral[500],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '14px',
  flexShrink: 0,
  textTransform: 'uppercase'
}))

// Quadrado para ícone/imagem de planos, recorrências, cobranças e contas a pagar
// (mesmo padrão visual da listagem de planos)
export const IconBox = styled('div')(() => ({
  display: 'grid',
  placeContent: 'center',
  boxSizing: 'border-box',
  width: 40,
  height: 40,
  minWidth: 40,
  background: '#F8F8F8',
  borderRadius: '8px',
  flexShrink: 0
}))

export const ResultContent = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flex: 1,
  minWidth: 0
}))

export const ResultType = styled('div')(({ color }: TypeProps) => ({
  color: theme.palette.neutral[400],
  fontWeight: 400,
  fontSize: '10px',
  backgroundColor: color,
  padding: 6,
  borderRadius: '20px'
}))

export const ResultsContainer = styled('div')(
  ({ hasResults }: ResultsProps) => ({
    visibility: hasResults ? 'visible' : 'hidden',
    position: 'relative',
    top: -4
  })
)

export const MobileSearchBox = styled('div')(() => ({
  backgroundColor: '#FFF',
  width: '100%',
  display: 'flex',
  padding: '12px 20px',
  borderRadius: '8px',
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.10)'
}))
